import React, { useCallback, useEffect, useMemo } from "react";
import { useAuthContext } from "@asgardeo/auth-react";
import { ArrowLeft, ChevronRight, FileText, Info } from "lucide-react";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import { Link, useLocation, useParams } from "react-router-dom";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import KnowledgeHubLayout from "../../layouts/KnowledgeHubLayout";

const PRESIGNED_BASE_URL = import.meta.env.VITE_PRESIGNED_BASE_URL;
const BASE_URL = "https://paralegal.lk";

const questionMarkdownFiles = import.meta.glob("./Questions/*.md", {
	eager: true,
	import: "default",
	query: "?raw",
});

const slugify = (value) =>
	(value || "")
		.toLowerCase()
		.normalize("NFKD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/-{2,}/g, "-")
		.replace(/^-+|-+$/g, "");

const cleanMarkdownText = (value) =>
	(value || "")
		.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
		.replace(/[*_`~]/g, "")
		.replace(/\\([.#-])/g, "$1")
		.replace(/\s+/g, " ")
		.trim();

const extractFirstParagraph = (lines) => {
	const paragraph = [];
	let started = false;

	for (const line of lines) {
		const value = line.trim();
		if (!value && !started) continue;
		if (!value && started) break;
		if (/^(#{1,6}|[-*+]\s|\d+\.\s|>)/.test(value)) continue;
		started = true;
		paragraph.push(value);
	}

	return cleanMarkdownText(paragraph.join(" ")).slice(0, 240);
};

const parseQuestionMarkdown = (markdown) => {
	const lines = (markdown || "").split(/\r?\n/);
	const titleIndex = lines.findIndex((line) => /^#\s+/.test(line.trim()));
	const title = titleIndex >= 0
		? cleanMarkdownText(lines[titleIndex].trim().replace(/^#\s+/, ""))
		: "Untitled";
	let bodyStart = titleIndex >= 0 ? titleIndex + 1 : 0;

	while (bodyStart < lines.length && !lines[bodyStart].trim()) bodyStart += 1;

	const descriptionLines = [];
	while (bodyStart < lines.length && /^>\s?/.test(lines[bodyStart].trim())) {
		descriptionLines.push(lines[bodyStart].trim().replace(/^>\s?/, ""));
		bodyStart += 1;
	}

	while (bodyStart < lines.length && !lines[bodyStart].trim()) bodyStart += 1;

	const contentLines = lines.slice(bodyStart);
	const description = cleanMarkdownText(descriptionLines.join(" "))
		|| extractFirstParagraph(contentLines)
		|| "Read the full legal research note and cited authorities.";

	return {
		title,
		description,
		content: contentLines.join("\n").trim(),
	};
};

const sortByNumericPrefixDesc = (a, b) => {
	const getNumber = (filename) => {
		const match = filename.match(/^(\d+)/);
		return match ? Number.parseInt(match[1], 10) : -Infinity;
	};

	return getNumber(b.filename) - getNumber(a.filename);
};

const extractHeadings = (markdown) =>
	(markdown || "")
		.split(/\r?\n/)
		.map((line) => line.match(/^(#{2,3})\s+(.+)$/))
		.filter(Boolean)
		.map((match) => {
			const label = cleanMarkdownText(match[2]);
			return {
				id: slugify(label),
				label,
				level: match[1].length,
			};
		})
		.filter((heading) => heading.id && heading.label);

const getTextContent = (children) =>
	React.Children.toArray(children)
		.map((child) => {
			if (typeof child === "string" || typeof child === "number") return child;
			if (React.isValidElement(child)) return getTextContent(child.props.children);
			return "";
		})
		.join("");

const isPdfHref = (href) => Boolean(href && /\.pdf(?:[?#].*)?$/i.test(href));

const createMarkdownComponents = (openPdfLink) => ({
	h1: ({ node, ...props }) => (
		<h2 className="mb-3 mt-8 font-display text-20 font-semibold text-pl-text-primary md:text-22" {...props} />
	),
	h2: ({ node, children, ...props }) => (
		<h2
			id={slugify(getTextContent(children))}
			className="mb-0 mt-8 scroll-mt-28 border-b border-pl-border pb-1 font-display text-20 font-semibold text-pl-text-primary first:mt-0 md:text-22"
			{...props}
		>
			{children}
		</h2>
	),
	h3: ({ node, children, ...props }) => (
		<h3
			id={slugify(getTextContent(children))}
			className="mb-2 mt-7 scroll-mt-28 text-16 font-semibold text-pl-text-primary"
			{...props}
		>
			{children}
		</h3>
	),
	h4: ({ node, ...props }) => (
		<h4 className="mb-2 mt-6 text-16 font-semibold text-pl-text-primary" {...props} />
	),
	p: ({ node, ...props }) => (
		<p className="mt-4 text-15 leading-8 text-pl-text-secondary" {...props} />
	),
	strong: ({ node, ...props }) => (
		<strong className="font-semibold text-pl-text-primary" {...props} />
	),
	ul: ({ node, ...props }) => (
		<ul className="mt-4 list-disc space-y-2 pl-6 text-15 leading-7 text-pl-text-secondary" {...props} />
	),
	ol: ({ node, ...props }) => (
		<ol className="mt-4 list-decimal space-y-2 pl-6 text-15 leading-7 text-pl-text-secondary" {...props} />
	),
	li: ({ node, ...props }) => <li {...props} />,
	blockquote: ({ node, ...props }) => (
		<blockquote className="mt-5 border-l-4 border-pl-accent/50 pl-4 italic text-pl-text-secondary" {...props} />
	),
	a: ({ node, ...props }) => (
		<a
			className="font-medium text-pl-accent underline underline-offset-2 hover:text-pl-accent-hover"
			onClick={(event) => {
				if (!isPdfHref(props.href)) return;
				event.preventDefault();
				openPdfLink(props.href);
			}}
			target="_blank"
			rel="noopener noreferrer"
			{...props}
		/>
	),
	code: ({ node, inline, className, children, ...props }) =>
		inline ? (
			<code className="rounded bg-pl-bg-subtle px-1 py-0.5 text-13" {...props}>
				{children}
			</code>
		) : (
			<code className={`block overflow-x-auto rounded-pl bg-pl-bg-subtle p-4 text-13${className ? ` ${className}` : ""}`} {...props}>
				{children}
			</code>
		),
	pre: ({ node, ...props }) => <pre className="mt-5 overflow-x-auto" {...props} />,
	table: ({ node, ...props }) => (
		<div className="mt-5 w-full overflow-x-auto rounded-pl border border-pl-border">
			<table className="w-full border-collapse text-left text-13" {...props} />
		</div>
	),
	thead: ({ node, ...props }) => <thead className="bg-pl-bg-subtle" {...props} />,
	tr: ({ node, ...props }) => <tr className="border-b border-pl-border last:border-b-0" {...props} />,
	th: ({ node, ...props }) => <th className="border-r border-pl-border px-3 py-2 font-semibold text-pl-text-primary last:border-r-0" {...props} />,
	td: ({ node, ...props }) => <td className="border-r border-pl-border px-3 py-2 align-top text-pl-text-secondary last:border-r-0" {...props} />,
	hr: ({ node, ...props }) => <hr className="my-7 border-pl-border" {...props} />,
});

const TableOfContents = ({ headings }) => (
	<nav aria-label="On this page" className="sticky top-[112px] border-l border-pl-border pl-5">
		<h2 className="mb-3 text-14 font-semibold text-pl-text-primary">On this page</h2>
		<ul className="space-y-2">
			{headings.map((heading) => (
				<li key={`${heading.level}-${heading.id}`}>
					<a
						href={`#${heading.id}`}
						className={`block text-13 leading-5 text-pl-text-tertiary hover:text-pl-accent ${heading.level === 3 ? "pl-3" : ""}`}
					>
						{heading.label}
					</a>
				</li>
			))}
		</ul>
	</nav>
);

const KnowledgeHub = () => {
	const { slug } = useParams();
	const location = useLocation();
	const { state, signIn, getAccessToken } = useAuthContext();

	const list = useMemo(() => {
		const items = Object.entries(questionMarkdownFiles).map(([path, markdown]) => {
			const filename = path.split("/").pop();
			const parsed = parseQuestionMarkdown(markdown);
			const numberMatch = filename.match(/^(\d+)/);
			const titleSlug = slugify(parsed.title);

			return {
				...parsed,
				filename,
				slug: numberMatch ? `${numberMatch[1]}-${titleSlug}` : titleSlug,
			};
		});

		return items.sort(sortByNumericPrefixDesc);
	}, []);

	const active = useMemo(
		() => (slug ? list.find((item) => item.slug === slug) || null : null),
		[list, slug]
	);
	const headings = useMemo(() => extractHeadings(active?.content), [active?.content]);
	const currentPath = `${location.pathname}${location.search}`;

	const openPDFWithPreSignedUrl = useCallback(async (originalLink) => {
		const newWindow = window.open("about:blank", "_blank");

		if (!PRESIGNED_BASE_URL) {
			if (newWindow) newWindow.location.href = originalLink;
			return;
		}

		try {
			let accessToken = "";
			try {
				accessToken = (await getAccessToken()) || "";
			} catch (tokenError) {
				console.error("Failed to get access token for KnowledgeHub PDF:", tokenError);
			}

			const response = await fetch(`${PRESIGNED_BASE_URL}/api/pdf/get-pdf-url`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
				},
				body: JSON.stringify({ link: originalLink }),
			});
			const data = await response.json();

			if (data.success && newWindow) {
				newWindow.location.href = data.presigned_url;
				return;
			}

			if (newWindow) newWindow.location.href = originalLink;
		} catch (error) {
			console.error("Error fetching KnowledgeHub pre-signed PDF URL:", error);
			if (newWindow) newWindow.location.href = originalLink;
		}
	}, [getAccessToken]);

	const openPdfLink = useCallback(async (href) => {
		if (!href) return;

		if (!state.isAuthenticated) {
			sessionStorage.setItem("redirectToDocument", href);
			sessionStorage.setItem("redirectAfterLogin", currentPath);
			signIn();
			return;
		}

		await openPDFWithPreSignedUrl(href);
	}, [currentPath, openPDFWithPreSignedUrl, signIn, state.isAuthenticated]);

	const markdownComponents = useMemo(
		() => createMarkdownComponents(openPdfLink),
		[openPdfLink]
	);

	useEffect(() => {
		if (!state.isAuthenticated) return;

		const redirectToDocument = sessionStorage.getItem("redirectToDocument");
		const redirectAfterLogin = sessionStorage.getItem("redirectAfterLogin");

		if (!redirectToDocument || redirectAfterLogin !== currentPath) return;

		openPDFWithPreSignedUrl(redirectToDocument);
		sessionStorage.removeItem("redirectToDocument");
		sessionStorage.removeItem("redirectAfterLogin");
	}, [currentPath, openPDFWithPreSignedUrl, state.isAuthenticated]);

	const pageTitle = active
		? `${active.title} | Knowledge Hub | paralegal.lk`
		: "Knowledge Hub | paralegal.lk";
	const pageDescription = active?.description
		|| "Explore detailed legal research, case law, and cited answers on Sri Lankan law.";

	if (slug && !active) {
		return (
			<KnowledgeHubLayout>
				<Helmet>
					<title>Research question not found | Knowledge Hub | paralegal.lk</title>
					<meta name="robots" content="noindex" />
				</Helmet>
				<section className="pb-20">
					<Link to="/knowledge-hub" className="mb-5 inline-flex items-center gap-2 text-14 text-pl-text-tertiary hover:text-pl-accent">
						<ArrowLeft size={16} aria-hidden="true" />
						All research questions
					</Link>
					<div className="rounded-pl-lg border border-pl-border bg-pl-bg-elevated p-6">
						<h1 className="font-display text-24 font-semibold text-pl-text-primary">Research question not found</h1>
						<p className="mt-3 text-15 text-pl-text-secondary">This research note may have moved or is no longer available.</p>
					</div>
				</section>
			</KnowledgeHubLayout>
		);
	}

	return (
		<div className="relative">
			<Helmet>
				<title>{pageTitle}</title>
				<meta name="description" content={pageDescription} />
				<link rel="canonical" href={`${BASE_URL}${location.pathname}`} />
				{active && (
					<script type="application/ld+json">{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "QAPage",
						mainEntity: {
							"@type": "Question",
							name: active.title,
							acceptedAnswer: {
								"@type": "Answer",
								text: active.content,
							},
						},
					})}</script>
				)}
			</Helmet>

			<KnowledgeHubLayout
				topContent={active ? (
					<nav aria-label="Breadcrumb" className="mb-5 flex items-center gap-2 text-14 text-pl-text-tertiary">
						<Link to="/knowledge-hub" className="hover:text-pl-accent">Knowledge Hub</Link>
						<ChevronRight size={14} aria-hidden="true" />
						<span aria-current="page">Research question</span>
					</nav>
				) : null}
				sidebar={active && headings.length > 0 ? <TableOfContents headings={headings} /> : null}
			>
				{!active ? (
					<section className="pb-20">
						<header className="border-b border-pl-border pb-8">
							<h1 className="font-display text-28 font-semibold text-pl-text-primary md:text-36">Knowledge Hub</h1>
							<p className="mt-3 max-w-2xl text-16 leading-7 text-pl-text-secondary">
								Explore detailed legal research, case law, and cited answers on Sri Lankan law.
							</p>
						</header>

						<div className="mt-8">
							<div className="mb-3 flex items-baseline justify-between gap-4">
								<h2 className="font-display text-22 font-semibold text-pl-text-primary">Research questions</h2>
								<span className="text-13 text-pl-text-tertiary">{list.length} articles</span>
							</div>
							<ul className="border-t border-pl-border">
								{list.map((item) => (
									<li key={item.filename} className="border-b border-pl-border">
										<Link
											to={`/knowledge-hub/${item.slug}`}
											className="group grid min-h-[112px] grid-cols-[24px_minmax(0,1fr)_20px] gap-3 py-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-pl-accent"
										>
											<FileText className="mt-1 text-pl-text-tertiary group-hover:text-pl-accent" size={18} aria-hidden="true" />
											<div className="min-w-0">
												<h3 className="text-16 font-semibold leading-6 text-pl-text-primary group-hover:text-pl-accent md:text-18">
													{item.title}
												</h3>
												<p className="mt-2 line-clamp-2 text-14 leading-6 text-pl-text-secondary">
													{item.description}
												</p>
											</div>
											<ChevronRight className="mt-1 text-pl-text-tertiary group-hover:text-pl-accent" size={18} aria-hidden="true" />
										</Link>
									</li>
								))}
							</ul>
						</div>
					</section>
				) : (
					<section className="pb-20">
						{headings.length > 0 && (
							<details className="mb-4 rounded-pl border border-pl-border bg-pl-bg-elevated px-4 py-3 lg:hidden">
								<summary className="cursor-pointer text-14 font-semibold text-pl-text-primary">On this page</summary>
								<ul className="mt-3 space-y-2 border-t border-pl-border pt-3">
									{headings.map((heading) => (
										<li key={`${heading.level}-${heading.id}`}>
											<a href={`#${heading.id}`} className={`block text-13 leading-5 text-pl-text-tertiary hover:text-pl-accent ${heading.level === 3 ? "pl-3" : ""}`}>
												{heading.label}
											</a>
										</li>
									))}
								</ul>
							</details>
						)}

						<article className="overflow-hidden rounded-pl-lg border border-pl-border bg-pl-bg-elevated shadow-pl-sm">
							<header className="border-b border-pl-border px-5 py-6 md:px-7 md:py-7">
								<p className="text-13 font-medium text-pl-accent">Research question</p>
								<h1 className="mt-2 font-display text-24 font-semibold leading-8 text-pl-text-primary md:text-28 md:leading-9">
									{active.title}
								</h1>
								<p className="mt-3 max-w-3xl text-15 leading-7 text-pl-text-secondary">
									{active.description}
								</p>
								<div role="note" className="mt-5 flex items-start gap-2 border-t border-pl-border pt-4 text-13 leading-5 text-pl-text-tertiary">
									<Info className="mt-0.5 flex-none" size={15} aria-hidden="true" />
									<div>
										<p>This answer was produced by our advanced AI legal research agent Researcher Silva 2.</p>
										<p className="mt-2">
											To gain access to this agent and our legal AI workspace, become a professional member at: {"  "}
											<Link to="/apply-for-membership" className="font-medium text-pl-accent underline underline-offset-2 hover:text-pl-accent-hover">
												Apply for professional membership
											</Link>
											.
										</p>
									</div>
								</div>
							</header>

							<div className="px-5 pb-8 pt-2 md:px-7 md:pb-10">
								<ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents}>
									{active.content}
								</ReactMarkdown>
							</div>
						</article>

						<Link to="/knowledge-hub" className="mt-6 inline-flex items-center gap-2 text-14 font-medium text-pl-accent hover:text-pl-accent-hover">
							<ArrowLeft size={16} aria-hidden="true" />
							All research questions
						</Link>
					</section>
				)}
			</KnowledgeHubLayout>
		</div>
	);
};

export default KnowledgeHub;
